import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { 
  User, 
  Check, 
  X, 
  Edit3, 
  AlertCircle,
  Loader2
} from 'lucide-react';

// Types
interface CohortMember {
  id: string;
  cohortId: string;
  sellerId: string;
  originalName: string;
  workingName: string;
  originalAddress: string;
  workingAddress: string;
  problemFlags: {
    isDuplicate?: 'NEEDS_REVIEW' | 'APPROVED' | 'DELETED';
    isLongName?: 'NEEDS_REVIEW' | 'APPROVED' | 'DELETED';
    isAllCaps?: 'NEEDS_REVIEW' | 'APPROVED' | 'DELETED';
    isAllSmall?: 'NEEDS_REVIEW' | 'APPROVED' | 'DELETED';
  };
  addressHelperData?: {
    addressType?: 'BUSINESS' | 'RESIDENTIAL' | 'UNKNOWN';
    latitude?: number;
    longitude?: number;
  };
}

interface NameResolverProps {
  members: CohortMember[];
  problemType: 'isLongName' | 'isAllCaps' | 'isAllSmall';
  onResolveProblem: (
    cohortMemberId: string, 
    problemToFix: string, 
    action: 'APPROVE' | 'DELETE', 
    updatedWorkingName?: string
  ) => Promise<void>;
}

const NameResolver: React.FC<NameResolverProps> = ({
  members,
  problemType,
  onResolveProblem
}) => {
  const [editingStates, setEditingStates] = useState<{ [key: string]: string }>({});
  const [processingStates, setProcessingStates] = useState<{ [key: string]: boolean }>({});

  // Get problem type label
  const getProblemTypeLabel = (type: string): string => {
    switch (type) {
      case 'isLongName': return 'Long Name';
      case 'isAllCaps': return 'All Caps Name';
      case 'isAllSmall': return 'All Small Name';
      default: return 'Name Issue';
    }
  };

  // Get problem type description
  const getProblemDescription = (type: string): string => {
    switch (type) {
      case 'isLongName': return 'Names that are too long and may need shortening';
      case 'isAllCaps': return 'Names in ALL CAPITAL LETTERS that need proper casing';
      case 'isAllSmall': return 'Names in all lowercase that need proper casing';
      default: return 'Names that need review';
    }
  };

  // Handle editing state changes
  const handleEditChange = (memberId: string, value: string) => {
    setEditingStates(prev => ({
      ...prev,
      [memberId]: value
    }));
  };

  // Handle approve action
  const handleApprove = async (member: CohortMember) => {
    const updatedName = editingStates[member.id] || member.workingName;
    
    setProcessingStates(prev => ({ ...prev, [member.id]: true }));
    
    try {
      await onResolveProblem(
        member.id,
        problemType,
        'APPROVE',
        updatedName
      );
      
      // Clear editing state after successful approval
      setEditingStates(prev => {
        const newState = { ...prev };
        delete newState[member.id];
        return newState;
      });
    } catch (error) {
      console.error('Error approving name:', error);
      // Error handling could be improved with toast notifications
    } finally {
      setProcessingStates(prev => ({ ...prev, [member.id]: false }));
    }
  };

  // Handle delete action
  const handleDelete = async (member: CohortMember) => {
    setProcessingStates(prev => ({ ...prev, [member.id]: true }));
    
    try {
      await onResolveProblem(
        member.id,
        problemType,
        'DELETE'
      );
    } catch (error) {
      console.error('Error deleting member:', error);
      // Error handling could be improved with toast notifications
    } finally {
      setProcessingStates(prev => ({ ...prev, [member.id]: false }));
    }
  };

  // Initialize editing state for a member
  const initializeEditing = (member: CohortMember) => {
    if (!editingStates[member.id]) {
      setEditingStates(prev => ({
        ...prev,
        [member.id]: member.workingName
      }));
    }
  };

  if (members.length === 0) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <User className="h-12 w-12 text-green-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            No {getProblemTypeLabel(problemType)} Issues
          </h3>
          <p className="text-gray-600 dark:text-gray-400">
            All names in this category have been resolved.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <AlertCircle className="h-5 w-5 text-yellow-600 mr-2" />
            {getProblemTypeLabel(problemType)} Resolution
          </CardTitle>
          <p className="text-gray-600 dark:text-gray-400">
            {getProblemDescription(problemType)}. Review each name and either approve with corrections or delete the entry.
          </p>
        </CardHeader>
      </Card>

      {/* Member Cards */}
      <div className="grid grid-cols-1 gap-4">
        {members.map((member) => {
          const isProcessing = processingStates[member.id];
          const isEditing = member.id in editingStates;
          const editedName = editingStates[member.id] || member.workingName;
          const hasChanges = editedName !== member.workingName;

          return (
            <Card key={member.id} className="border-l-4 border-l-yellow-500">
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    {/* Seller Info */}
                    <div className="flex items-center mb-3">
                      <div className="p-2 bg-gray-100 dark:bg-gray-800 rounded-lg mr-3">
                        <User className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                          Seller ID: {member.sellerId}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-500">
                          Address: {member.workingAddress}
                        </p>
                      </div>
                    </div>

                    {/* Name Comparison */}
                    <div className="space-y-2 mb-4">
                      <div>
                        <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                          Original Name
                        </label>
                        <p className="text-sm text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-800 p-2 rounded">
                          {member.originalName}
                        </p>
                      </div>
                      
                      <div>
                        <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                          Working Name {hasChanges && <Badge variant="secondary" className="ml-1 text-xs">Modified</Badge>}
                        </label>
                        <div className="flex items-center space-x-2">
                          <Input
                            value={editedName}
                            onChange={(e) => handleEditChange(member.id, e.target.value)}
                            onFocus={() => initializeEditing(member)}
                            className="text-sm"
                            placeholder="Enter corrected name..."
                          />
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => initializeEditing(member)}
                            className="p-2"
                          >
                            <Edit3 className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-col space-y-2 ml-4">
                    <Button
                      onClick={() => handleApprove(member)}
                      disabled={isProcessing || !editedName.trim()}
                      size="sm"
                      className="bg-green-600 hover:bg-green-700 text-white"
                    >
                      {isProcessing ? (
                        <Loader2 className="h-3 w-3 animate-spin mr-1" />
                      ) : (
                        <Check className="h-3 w-3 mr-1" />
                      )}
                      Approve
                    </Button>
                    
                    <Button
                      onClick={() => handleDelete(member)}
                      disabled={isProcessing}
                      variant="destructive"
                      size="sm"
                    >
                      {isProcessing ? (
                        <Loader2 className="h-3 w-3 animate-spin mr-1" />
                      ) : (
                        <X className="h-3 w-3 mr-1" />
                      )}
                      Delete
                    </Button>
                  </div>
                </div>

                {/* Problem Type Badge */}
                <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                  <Badge variant="secondary" className="bg-yellow-50 text-yellow-700 dark:bg-yellow-900/10 dark:text-yellow-400">
                    {getProblemTypeLabel(problemType)}
                  </Badge>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default NameResolver;